import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "../components/ui/dialog";
import { AlertCircle, CheckCircle, XCircle, Send, Loader2, Undo, Check, Pencil, Edit3, ClipboardCheck, FileText } from "lucide-react";
import { format } from "date-fns";
import EstimateLineItems from "../components/estimates/estimate-line-items";
import { generatePdfHtml, PdfTemplateConfig, DEFAULT_PDF_TEMPLATE_CONFIG } from "../components/pdf/unified-pdf-template";
import { formatPhone } from "../lib/validation";
import { useToast } from "../hooks/use-toast";
import { downloadEstimatePdf } from "../lib/pdf-download";
import { getDisplayNotes, sanitizeEstimateDescription } from "../lib/estimate-notes";

// Componente para firmar digitalmente con touchscreen
function SignaturePad({
  onSave,
  onCancel,
  width = 350,
  height = 200,
  lineWidth = 2.5,
  lineColor = "#000000",
  clearLabel = "Clear",
  confirmLabel = "Confirm Signature"
}: {
  onSave: (value: string) => void;
  onCancel: () => void;
  width?: number;
  height?: number;
  lineWidth?: number;
  lineColor?: string;
  clearLabel?: string;
  confirmLabel?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const isFirstRender = useRef(true);
  
  // Detectar si es dispositivo móvil
  useEffect(() => {
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
  }, []);
  
  // Inicializar el canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Configurar el contexto del canvas
    const context = canvas.getContext('2d');
    if (!context) return;
    
    // Establecer las dimensiones exactas del canvas con mayor resolución
    const scale = window.devicePixelRatio || 2; // Usar devicePixelRatio para mejor calidad
    canvas.width = width * scale;
    canvas.height = height * scale;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    
    // Escalar el contexto para la alta resolución
    context.scale(scale, scale);
    
    // Configurar el contexto de dibujo
    context.lineWidth = lineWidth;
    context.strokeStyle = lineColor;
    context.lineJoin = 'round';
    context.lineCap = 'round';
    context.fillStyle = '#ffffff'; // Fondo blanco
    
    setCtx(context);
    
    // Limpiar el canvas si es primera renderización
    if (isFirstRender.current) {
      clearCanvas();
      isFirstRender.current = false;
    }
    
    // No need to load previous value since we're creating a new signature
  }, [width, height, lineWidth, lineColor]);
  
  // Función para comenzar a dibujar
  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setDrawing(true);
    draw(e);
  };
  
  // Función para dibujar
  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing || !ctx) return;
    
    // Prevenir desplazamiento en dispositivos táctiles
    if (e.nativeEvent instanceof TouchEvent) {
      e.preventDefault();
    }
    
    // Obtener coordenadas
    let x: number, y: number;
    
    if ('touches' in e.nativeEvent) {
      // Evento táctil
      const touch = e.nativeEvent.touches[0];
      const rect = canvasRef.current?.getBoundingClientRect();
      x = touch.clientX - (rect?.left || 0);
      y = touch.clientY - (rect?.top || 0);
    } else {
      // Evento de ratón
      const rect = canvasRef.current?.getBoundingClientRect();
      x = e.nativeEvent.clientX - (rect?.left || 0);
      y = e.nativeEvent.clientY - (rect?.top || 0);
    }
    
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
    
    setHasDrawn(true);
  };
  
  // Función para dejar de dibujar
  const finishDrawing = () => {
    if (!ctx) return;
    
    ctx.beginPath();
    setDrawing(false);
  };
  
  // Función para limpiar el canvas
  const clearCanvas = () => {
    if (!ctx || !canvasRef.current) return;
    
    // Llenar con fondo blanco en lugar de solo limpiar
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    ctx.fillStyle = '#000000'; // Restaurar color de línea
    ctx.beginPath();
    setHasDrawn(false);
  };
  
  // Función para guardar la firma
  const saveSignature = () => {
    if (!canvasRef.current || !hasDrawn) return;
    
    try {
      // Generar imagen PNG de alta calidad
      const dataURL = canvasRef.current.toDataURL('image/png', 1.0);
      onSave(dataURL);
    } catch (e) {
      console.error('Error al guardar la firma:', e);
    }
  };
  
  return (
    <div className="flex flex-col items-center w-full">
      {/* Centered Signature Canvas */}
      <div className="flex justify-center w-full mb-4">
        <div 
          className="border-2 border-green-400 rounded-lg overflow-hidden bg-white relative shadow-lg"
          style={{ width: `${width}px`, height: `${height}px`, touchAction: 'none' }}
        >
          <canvas
            ref={canvasRef}
            className="cursor-crosshair"
            width={width}
            height={height}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={finishDrawing}
            onMouseLeave={finishDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={finishDrawing}
          />
          
          {!hasDrawn && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 pointer-events-none">
              <div className="text-center p-4">
                <Edit3 className="h-6 w-6 mx-auto mb-2 opacity-50" />
                <p className="text-sm sm:text-base font-medium">Draw your signature here</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Centered Large Buttons for Easy Tapping */}
      <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
        <Button 
          type="button" 
          variant="outline" 
          onClick={clearCanvas}
          className="flex items-center justify-center gap-2 min-h-[48px] px-6 border-2 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
        >
          <Undo className="h-5 w-5" />
          <span className="font-semibold">{clearLabel}</span>
        </Button>
        
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          className="flex items-center justify-center gap-2 min-h-[48px] px-6 border-2 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
        >
          <XCircle className="h-5 w-5" />
          <span className="font-semibold">Cancel</span>
        </Button>
        
        <Button 
          type="button" 
          onClick={saveSignature}
          className="flex items-center justify-center gap-2 min-h-[48px] px-6 bg-green-500 hover:bg-green-600 text-white font-bold"
          disabled={!hasDrawn}
        >
          <Check className="h-5 w-5" />
          <span>{confirmLabel}</span>
        </Button>
      </div>
      
      {isMobile && (
        <p className="text-sm sm:text-base text-green-600 font-medium mt-3 text-center">
          ✍️ Use your finger or a stylus to sign
        </p>
      )}
    </div>
  );
}

// Colores personalizados según el estado
const statusColors: Record<string, string> = {
  draft: "bg-gray-400",
  sent: "bg-blue-400",
  accepted: "bg-green-400",
  rejected: "bg-red-400",
  converted: "bg-purple-400"
};

const statusIcons: Record<string, React.ReactNode> = {
  draft: <AlertCircle className="h-4 w-4" />,
  sent: <Send className="h-4 w-4" />,
  accepted: <CheckCircle className="h-4 w-4" />,
  rejected: <XCircle className="h-4 w-4" />,
  converted: <CheckCircle className="h-4 w-4" />
};

export default function PublicEstimateView() {
  const { id } = useParams<{ id: string }>();
  const [location] = useLocation();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [estimate, setEstimate] = useState<any>(null);
  const [contractor, setContractor] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [pdfConfig, setPdfConfig] = useState<PdfTemplateConfig | null>(null);
  // Ya no necesitamos variables para la firma
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [signedEstimate, setSignedEstimate] = useState<any>(null);
  const [actionComplete, setActionComplete] = useState(false);
  const [actionResult, setActionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  // Store token in state for use when signing (avoids URL being modified by email clients or navigation)
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Extract token from URL - check both search and hash (some email clients or routing may put it in hash)
  const getTokenFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    let token = params.get('token');
    if (!token && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      token = hashParams.get('token');
    }
    return token;
  };

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        setLoading(true);
        
        // SECURITY: Require token for public access
        const token = getTokenFromUrl();
        if (!token) {
          setError("Access token is required. Please use the link provided in your email.");
          setLoading(false);
          return;
        }
        
        // Store token for use when signing (avoids URL being modified by email clients or navigation)
        setAccessToken(token);
        
        const response = await fetch(`/api/protected/estimates?id=${id}&public=true&token=${encodeURIComponent(token)}`);
        
        if (!response.ok) {
          // Try to get more specific error message from response
          let errorMessage = "Hubo un problema al cargar el estimado.";
          try {
            const errorData = await response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (e) {
            // Use default message if JSON parsing fails
          }
          
          throw new Error(
            response.status === 404
              ? "Estimado no encontrado. Por favor verifique el enlace."
              : response.status === 401
              ? errorMessage || "Token de acceso inválido o expirado. Por favor use el enlace del correo electrónico."
              : errorMessage
          );
        }
        
        const data = await response.json();
        console.log('🔍 DEBUG: Public estimate data received:', data);
        console.log('🔍 DEBUG: Estimate items:', data.items);
        console.log('🔍 DEBUG: Estimate structure:', JSON.stringify(data, null, 2));
        setEstimate(data);
        
        if (data.contractor) {
          setContractor(data.contractor);
        }
        
        if (data.client) {
          setClient(data.client);
        }
        
        // Fetch contractor's PDF template config (non-critical; use default if fails)
        if (data.contractorId) {
          try {
            const settingsResponse = await fetch(`/api/public/contractor/${data.contractorId}/settings`);
            if (settingsResponse.ok) {
              const contentType = settingsResponse.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const text = await settingsResponse.text();
                try {
                  const settingsData = JSON.parse(text);
                  if (settingsData?.pdfTemplate) {
                    setPdfConfig(settingsData.pdfTemplate);
                  }
                } catch {
                  // Response claimed JSON but wasn't (e.g. HTML error page) - use default
                }
              }
            }
          } catch {
            // Network error or fetch failed - use default config silently
          }
        }
        
        setError(null);
      } catch (error) {
        console.error("Error fetching estimate:", error);
        setError(error instanceof Error ? error.message : "Error desconocido");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEstimate();
    }
  }, [id]);

  // Auto-open signature dialog if action=approve is in URL
  useEffect(() => {
    if (!loading && estimate && !estimate.clientSignature) {
      const params = new URLSearchParams(window.location.search);
      const action = params.get('action');
      if (action === 'approve') {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
          setSignatureDialogOpen(true);
        }, 500);
      }
    }
  }, [loading, estimate]);

  const handleAction = async (action: 'accept' | 'reject') => {
    if (action === 'reject' && !notes) {
      setError("Por favor, proporcione un motivo para el rechazo.");
      return;
    }

    try {
      setActionInProgress(true);
      setError(null);
      
      // SECURITY: Use stored token (from initial load) or fallback to URL
      const token = accessToken || getTokenFromUrl();
      if (!token) {
        setError("Session expired. Please use the link from your email again.");
        return;
      }
      
      const requestBody = {
        estimateId: id,
        action,
        clientId: client?.id ?? estimate?.clientId ?? estimate?.client_id,
        notes: action === 'accept' ? '' : notes,
        signature: action === 'accept' ? signatureData : undefined,
        token: token  // Include token for backend validation
      };
      
      console.log('🔍 Sending request:', requestBody);
      console.log('🔍 Signature data:', signatureData);
      
      const response = await fetch(`/api/client-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || "Hubo un problema al procesar su solicitud");
      }
      
      // Update estimate with full data from response to avoid needing to refetch (which would require token)
      if (result.estimate) {
        setEstimate(prevEstimate => ({
          ...prevEstimate,
          ...result.estimate,
          status: result.estimate.status,
          clientSignature: result.estimate.clientSignature || result.estimate.client_signature,
          client_signature: result.estimate.clientSignature || result.estimate.client_signature,
          updatedAt: result.estimate.updatedAt || new Date().toISOString()
        }));
      }
      setActionResult({
        success: true,
        message: action === 'accept' 
                      ? "Estimate accepted! Thank you for your confirmation."
            : "Estimate rejected. We have recorded your decision."
      });
      setActionComplete(true);
      
      // Show success dialog for accepted estimates
      if (action === 'accept') {
        setSignedEstimate(result.estimate);
        setSuccessDialogOpen(true);
      }
      
      // Cerrar diálogos
      setAcceptDialogOpen(false);
      setRejectDialogOpen(false);
      setSignatureDialogOpen(false);
      
    } catch (error) {
      console.error(`Error ${action === 'accept' ? 'accepting' : 'rejecting'} estimate:`, error);
      setActionResult({
        success: false,
        message: error instanceof Error ? error.message : "Error desconocido"
      });
    } finally {
      setActionInProgress(false);
    }
  };

  const formatCurrency = (value: number | string) => {
    // Handle NaN, null, undefined values
    const numValue = Number(value);
    const validAmount = isNaN(numValue) || numValue === null || numValue === undefined ? 0 : numValue;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(validAmount);
  };

  const handleActionWithSignature = async (action: 'accept' | 'reject', signature: string) => {
    // SECURITY: Use stored token (from initial load) or fallback to URL
    const token = accessToken || getTokenFromUrl();
    const clientId = client?.id ?? estimate?.clientId ?? estimate?.client_id;
    if (!token) {
      toast({
        title: "Cannot submit",
        description: "Please use the exact link from your email to sign this estimate. The link contains a secure token.",
        variant: "destructive",
      });
      return;
    }
    if (!clientId) {
      toast({
        title: "Cannot submit",
        description: "Something went wrong loading this estimate. Please refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      setActionInProgress(true);
      setError(null);
      
      const requestBody = {
        estimateId: id,
        action,
        clientId,
        notes: action === 'accept' ? '' : notes,
        signature: action === 'accept' ? signature : undefined,
        token,
      };
      
      console.log('🔍 Sending request with signature:', { ...requestBody, signature: signature ? '[present]' : undefined });
      
      const response = await fetch(`/api/client-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      let result: { message?: string; error?: string; estimate?: any } = {};
      try {
        result = await response.json();
      } catch {
        // Server may return non-JSON (e.g. HTML error page)
      }
      
      const errorMessage = result.message || result.error || "Something went wrong. Please try again.";
      
      if (!response.ok) {
        throw new Error(errorMessage);
      }
      
      // Update the existing estimate with the new status and signature
      const updated = result.estimate;
      if (updated) {
        setEstimate(prevEstimate => ({
          ...prevEstimate,
          ...updated,
          status: updated.status,
          clientSignature: updated.clientSignature,
          client_signature: updated.clientSignature,
          updatedAt: updated.updatedAt || new Date().toISOString()
        }));
      } else {
        setEstimate(prevEstimate => ({
          ...prevEstimate,
          status: action === 'accept' ? 'accepted' : 'rejected',
          clientSignature: action === 'accept' ? signature : prevEstimate?.clientSignature,
          client_signature: action === 'accept' ? signature : prevEstimate?.client_signature,
          updatedAt: new Date().toISOString()
        }));
      }
      
      setActionResult({
        success: true,
        message: action === 'accept' 
          ? "Estimate accepted! Thank you for your confirmation."
          : "Estimate rejected. We have recorded your decision."
      });
      setActionComplete(true);
      
      if (action === 'accept') {
        setSignedEstimate(updated || estimate);
        setAcceptDialogOpen(false);
        setSignatureDialogOpen(false);
        setSuccessDialogOpen(true);
      } else {
        setRejectDialogOpen(false);
      }
      
    } catch (error) {
      console.error(`Error ${action === 'accept' ? 'accepting' : 'rejecting'} estimate:`, error);
      const message = error instanceof Error ? error.message : "Unknown error";
      const isNetworkError = error instanceof TypeError && (error.message === "Failed to fetch" || error.message.includes("NetworkError"));
      const userMessage = isNetworkError
        ? "Check your internet connection and try again."
        : message;
      setActionResult({
        success: false,
        message: userMessage
      });
      toast({
        title: action === 'accept' ? "Could not submit signature" : "Could not submit rejection",
        description: userMessage,
        variant: "destructive"
      });
    } finally {
      setActionInProgress(false);
    }
  };

  const generateSignedPDF = () => {
    try {
      if (!estimate) {
        toast({
          title: "Error",
          description: "Estimate data is not available. Please refresh the page.",
          variant: "destructive"
        });
        return;
      }

      // Same template as print and client view
      const config = pdfConfig ? { ...DEFAULT_PDF_TEMPLATE_CONFIG, ...pdfConfig } : DEFAULT_PDF_TEMPLATE_CONFIG;

      downloadEstimatePdf(estimate, config);
      
      toast({
        title: "PDF Download",
        description: "Print dialog opened. Select 'Save as PDF' to download.",
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to download PDF. Please try again.",
        variant: "destructive"
      });
    }

    /* OLD HARDCODED TEMPLATE - REPLACED WITH UNIFIED
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Signed Estimate - ${estimate.estimateNumber}</title>
        <style>
          body { 
            font-family: 'Times New Roman', serif; 
            margin: 0; 
            padding: 40px; 
            line-height: 1.6; 
            color: #333;
            background: white;
          }
          .header { 
            text-align: center; 
            margin-bottom: 40px; 
            border-bottom: 3px solid ${BRAND_COLORS.primary}; 
            padding-bottom: 20px; 
          }
          .company-name { 
            font-size: 28px; 
            font-weight: bold; 
            color: ${BRAND_COLORS.primary}; 
            margin-bottom: 10px;
            letter-spacing: 2px;
          }
          .document-title { 
            font-size: 20px; 
            font-weight: bold; 
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .estimate-number { 
            font-size: 16px; 
            color: #666;
          }
          .section { 
            margin-bottom: 30px; 
          }
          .section-title { 
            font-size: 18px; 
            font-weight: bold; 
            color: #2563eb; 
            margin-bottom: 15px;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 5px;
          }
          .info-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 30px; 
            margin-bottom: 30px; 
          }
          .info-item { 
            margin-bottom: 10px; 
          }
          .info-label { 
            font-weight: bold; 
            color: #374151;
          }
          .items-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 30px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .items-table th, .items-table td { 
            border: 1px solid #d1d5db; 
            padding: 12px; 
            text-align: left; 
          }
          .items-table th { 
            background-color: #f9fafb; 
            font-weight: bold;
            color: #374151;
          }
          .total-section { 
            text-align: right; 
            margin: 30px 0; 
            padding: 20px;
            background-color: #f9fafb;
            border-radius: 8px;
          }
          .total-amount { 
            font-size: 24px; 
            font-weight: bold; 
            color: #059669;
          }
          .signature-section { 
            margin-top: 60px; 
            border-top: 2px solid #2563eb; 
            padding-top: 30px; 
          }
          .signature-container { 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-start;
            margin-top: 30px;
          }
          .signature-box { 
            width: 400px; 
            height: 180px; 
            border: 2px solid #374151; 
            border-radius: 8px;
            padding: 15px;
            background-color: #ffffff;
            margin: 0 auto;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .signature-image { 
            width: 100%; 
            height: 100%; 
            object-fit: contain; 
            display: block;
            image-rendering: high-quality;
            image-rendering: -webkit-optimize-contrast;
          }
          .signature-details { 
            margin-top: 15px; 
            font-size: 14px;
          }
          .legal-notice { 
            margin-top: 40px; 
            padding: 20px; 
            background-color: #dcfce7; 
            border: 1px solid #22c55e; 
            border-radius: 8px;
            font-size: 14px;
            color: #92400e;
          }
          .footer { 
            margin-top: 50px; 
            text-align: center; 
            font-size: 12px; 
            color: #6b7280;
            border-top: 1px solid #e5e7eb;
            padding-top: 20px;
          }
          @media print { 
            body { margin: 0; padding: 20px; } 
            .header { page-break-inside: avoid; }
            .signature-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">REMODRA</div>
          <div class="document-title">Signed Service Contract</div>
          <div class="estimate-number">Contract #${estimate.estimateNumber}</div>
        </div>
        
        <div class="info-grid">
          <div class="section">
            <div class="section-title">Contract Information</div>
            <div class="info-item">
              <span class="info-label">Issue Date:</span> ${currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div class="info-item">
              <span class="info-label">Expiry Date:</span> ${new Date(estimate.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div class="info-item">
              <span class="info-label">Status:</span> <strong>ACCEPTED & SIGNED</strong>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Client Information</div>
            <div class="info-item">
              <span class="info-label">Name:</span> ${estimate.client.firstName} ${estimate.client.lastName}
            </div>
            <div class="info-item">
              <span class="info-label">Email:</span> ${estimate.client.email}
            </div>
            <div class="info-item">
              <span class="info-label">Phone:</span> ${estimate.client.phone ? formatPhone(estimate.client.phone) : 'N/A'}
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Services & Pricing</div>
          <table class="items-table">
            <thead>
              <tr>
                <th>Service Type</th>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${estimate.items.map(item => `
                <tr>
                  <td><strong>${item.serviceType.charAt(0).toUpperCase() + item.serviceType.slice(1)}</strong></td>
                  <td>${sanitizeEstimateDescription(item.description)}</td>
                  <td>${item.quantity} ${item.unit}</td>
                  <td>${formatCurrency(item.unitPrice)}</td>
                  <td><strong>${formatCurrency(item.amount)}</strong></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="total-section">
            <div style="font-size: 18px; margin-bottom: 10px;">Total Contract Amount:</div>
            <div class="total-amount">${formatCurrency(estimate.total)}</div>
          </div>
        </div>
        
        ${estimate.description ? `
        <div class="section">
          <div class="section-title">Project Description</div>
          <p style="line-height: 1.8; color: #4b5563;">${estimate.description}</p>
        </div>
        ` : ''}
        
        ${(() => { const n = getDisplayNotes(estimate.notes); return n ? `
        <div class="section">
          <div class="section-title">Additional Notes</div>
          <p style="line-height: 1.8; color: #4b5563;">${n}</p>
        </div>
        ` : ''; })()}
        
        ${estimate.clientSignature ? `
        <div class="signature-section">
          <div class="section-title">Digital Signature & Authorization</div>
          <p style="margin-bottom: 20px; line-height: 1.8;">
            By signing below, the client acknowledges that they have read, understood, and agree to the terms 
            and conditions outlined in this service contract. This signature constitutes acceptance of the 
            proposed work and pricing.
          </p>
          
          <div class="signature-container">
            <div>
              <div class="signature-box">
                <img src="${estimate.clientSignature}" class="signature-image" alt="Client Signature" />
              </div>
              <div class="signature-details">
                <div><strong>Signed by:</strong> ${estimate.client.firstName} ${estimate.client.lastName}</div>
                <div><strong>Date:</strong> ${signatureDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                <div><strong>Time:</strong> ${signatureDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
                <div><strong>IP Address:</strong> ${window.location.hostname}</div>
              </div>
            </div>
          </div>
        </div>
        ` : ''}
        
        <div class="legal-notice">
          <strong>Legal Notice:</strong> This document constitutes a legally binding contract between 
          Remodra and the client. The client's digital signature above indicates acceptance of all terms, 
          conditions, and pricing outlined in this contract. Both parties agree to fulfill their obligations 
          as specified herein.
        </div>
        
        <div class="footer">
          <p><strong>Remodra Service Contract</strong> | Generated on ${currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          <p>This document is legally binding and serves as proof of agreement between both parties.</p>
        </div>
      </body>
      </html>
    `; */

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print dialog (user can Save as PDF)
    setTimeout(() => {
      printWindow.print();
    }, 250);
    
    toast({
      title: "PDF Download",
      description: "Print dialog opened. Select 'Save as PDF' to download.",
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-green-500" />
          <p className="text-lg text-slate-800 sm:text-slate-200">Loading estimate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Card className="w-full max-w-3xl shadow-lg remodra-card">
          <CardHeader className="bg-red-900/20 border-red-500/20">
            <CardTitle className="text-red-400 flex items-center gap-2">
              <AlertCircle className="h-6 w-6" />
              Error
            </CardTitle>
            <CardDescription className="text-slate-600 sm:text-slate-300">Could not load the estimate</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-lg text-center mb-6 text-slate-800 sm:text-slate-200">{error}</p>
            <div className="flex justify-center">
              <Button onClick={() => window.location.reload()} className="remodra-button">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Card className="w-full max-w-3xl shadow-lg remodra-card">
          <CardHeader>
            <CardTitle className="text-green-500">Estimate Not Found</CardTitle>
            <CardDescription className="text-slate-600 sm:text-slate-300">Could not find the requested estimate</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-slate-800 sm:text-slate-200">The estimate you are looking for does not exist or has been deleted.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si ya se ha tomado acción y se ha completado la acción actual
  if (actionComplete) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Card className="w-full max-w-4xl shadow-lg remodra-card">
          <CardHeader className={
            estimate.status === "accepted" || actionResult?.success
              ? "bg-green-900/20 border-green-500/20"
              : estimate.status === "rejected"
              ? "bg-red-900/20 border-red-500/20"
              : "bg-blue-900/20 border-blue-500/20"
          }>
            <CardTitle className={
              estimate.status === "accepted" || actionResult?.success
                ? "text-green-400 flex items-center gap-2"
                : estimate.status === "rejected"
                ? "text-red-400 flex items-center gap-2"
                : "text-blue-400 flex items-center gap-2"
            }>
              {estimate.status === "accepted" && <CheckCircle className="h-6 w-6" />}
              {estimate.status === "rejected" && <XCircle className="h-6 w-6" />}
              {estimate.status !== "accepted" && estimate.status !== "rejected" && <AlertCircle className="h-6 w-6" />}
              
              {estimate.status === "accepted" && "Estimate Accepted"}
              {estimate.status === "rejected" && "Estimate Rejected"}
              {estimate.status !== "accepted" && estimate.status !== "rejected" && "Current Status"}
            </CardTitle>
            <CardDescription className="text-slate-600 sm:text-slate-300">
              {estimate.status === "accepted" && "This estimate has been accepted."}
              {estimate.status === "rejected" && "This estimate has been rejected."}
              {estimate.status === "draft" && "This estimate is in draft."}
              {estimate.status === "converted" && "This estimate has been converted to invoice."}
              {estimate.status === "sent" && actionComplete && actionResult?.message}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-green-500">Estimate Details</h3>
                <p className="text-slate-800 sm:text-slate-200"><span className="font-medium text-slate-600 sm:text-slate-300">Number:</span> {estimate.estimateNumber}</p>
                <p className="text-slate-800 sm:text-slate-200"><span className="font-medium text-slate-600 sm:text-slate-300">Date:</span> {estimate.issueDate ? format(new Date(estimate.issueDate), 'MMM dd, yyyy') : 'Not available'}</p>
                <p className="text-slate-800 sm:text-slate-200"><span className="font-medium text-slate-600 sm:text-slate-300">Expiration:</span> {estimate.expiryDate ? format(new Date(estimate.expiryDate), 'MMM dd, yyyy') : 'Not available'}</p>
                <p className="flex items-center gap-1">
                  <span className="font-medium text-slate-600 sm:text-slate-300">Status:</span> 
                  <Badge className={`${statusColors[estimate.status]} text-white flex gap-1 items-center`}>
                    {statusIcons[estimate.status]} 
                    {estimate.status === 'draft' && 'Draft'}
                    {estimate.status === 'sent' && 'Sent'}
                    {estimate.status === 'accepted' && 'Accepted'}
                    {estimate.status === 'rejected' && 'Rejected'}
                    {estimate.status === 'converted' && 'Converted'}
                  </Badge>
                </p>
              </div>
              <div>
                {contractor && (
                  <>
                    <h3 className="text-lg font-semibold mb-2 text-green-500">Contractor</h3>
                    <p className="text-slate-800 sm:text-slate-200">{contractor.companyName || `${contractor.firstName} ${contractor.lastName}`}</p>
                    <p className="text-slate-800 sm:text-slate-200">{contractor.email}</p>
                    <p className="text-slate-800 sm:text-slate-200">{formatPhone(contractor.phone)}</p>
                  </>
                )}
                <div className="mt-4">
                  {client && (
                    <>
                      <h3 className="text-lg font-semibold mb-2 text-green-500">Client</h3>
                      <p className="text-slate-800 sm:text-slate-200">{client.firstName} {client.lastName}</p>
                      <p className="text-slate-800 sm:text-slate-200">{client.email}</p>
                      <p className="text-slate-800 sm:text-slate-200">{formatPhone(client.phone)}</p>
                      <p className="text-slate-800 sm:text-slate-200">{client.address}</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <Separator className="bg-slate-600" />

            <div>
              <h3 className="text-lg font-semibold mb-4 text-green-500">Item Details</h3>
              <EstimateLineItems 
                items={estimate.items || []}
                estimateNumber={estimate.estimateNumber || estimate.estimate_number || 'N/A'}
                className="mb-6"
              />
              
              {/* Item Notes */}
              {estimate.items?.some((item: any) => getDisplayNotes(item.notes)) && (
                <div className="mt-4">
                  <h4 className="text-md font-semibold mb-2 text-green-500">Item Notes</h4>
                  <div className="space-y-2">
                    {estimate.items?.map((item: any) => (
                      getDisplayNotes(item.notes) && (
                        <div key={item.id} className="bg-slate-800/50 p-3 rounded-md border border-slate-600">
                          <span className="font-medium text-slate-800 sm:text-slate-200">{item.serviceType || 'General Service'}: </span>
                          <span className="text-slate-600 sm:text-slate-300">{getDisplayNotes(item.notes)}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <div className="w-full max-w-xs">
                <div className="flex justify-between py-1">
                  <span className="text-slate-800 sm:text-slate-200">Subtotal:</span>
                  <span className="text-slate-800 sm:text-slate-200">{formatCurrency(estimate.subtotal)}</span>
                </div>
                {Number(estimate.tax) > 0 && (
                  <div className="flex justify-between py-1">
                    <span className="text-slate-800 sm:text-slate-200">Tax ({estimate.tax}%):</span>
                    <span className="text-slate-800 sm:text-slate-200">{formatCurrency((Number(estimate.subtotal) * Number(estimate.tax) / 100))}</span>
                  </div>
                )}
                {Number(estimate.discount) > 0 && (
                  <div className="flex justify-between py-1">
                    <span className="text-slate-800 sm:text-slate-200">Discount:</span>
                    <span className="text-slate-800 sm:text-slate-200">-{formatCurrency(Number(estimate.discount))}</span>
                  </div>
                )}
                <Separator className="my-2 bg-slate-600" />
                <div className="flex justify-between py-1 font-bold">
                  <span className="text-green-500">Total:</span>
                  <span className="text-green-500">{formatCurrency(estimate.total)}</span>
                </div>
              </div>
            </div>

            {estimate.terms && (
              <div>
                <h3 className="text-lg font-semibold mb-2 text-green-500">Terms</h3>
                <div className="bg-slate-800/50 p-4 rounded-md whitespace-pre-wrap text-slate-800 sm:text-slate-200 border border-slate-600">
                  {estimate.terms}
                </div>
              </div>
            )}

            {getDisplayNotes(estimate.notes) && (
              <div>
                <h3 className="text-lg font-semibold mb-2 text-green-500">Notes</h3>
                <div className="bg-slate-800/50 p-4 rounded-md whitespace-pre-wrap text-slate-800 sm:text-slate-200 border border-slate-600">
                  {getDisplayNotes(estimate.notes)}
                </div>
              </div>
            )}

            {estimate.clientSignature && (
              <div>
                <h3 className="text-lg font-semibold mb-2 text-green-500">Signed by client</h3>
                <div className="bg-slate-800/50 p-4 rounded-md text-slate-800 sm:text-slate-200 border border-slate-600">
                  <img 
                    src={estimate.clientSignature} 
                    alt="Client signature" 
                    className="max-h-32 mx-auto" 
                  />
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-800/50 p-6 border-t border-slate-600">
            <p className="text-sm text-slate-600 sm:text-slate-300">
              {estimate.status === "accepted" && "This estimate has been accepted."}
              {estimate.status === "rejected" && "This estimate has been rejected."}
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Si todavía no se ha tomado acción (estimate.status === 'sent')
  const companyName = contractor?.companyName || contractor?.name || 'REMODRA';
  const documentTitle = estimate.clientSignature ? 'Signed Service Contract' : 'ESTIMATE';
  const documentNumber = estimate.estimateNumber || estimate.estimate_number || `EST-${estimate.id || 'UNKNOWN'}`;
  
  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-10">
      <div className="max-w-5xl mx-auto">
        {/* Header - Centered like PDF template */}
        <div className="text-center mb-8">
          {contractor?.logoUrl && (
            <div className="mb-4 flex justify-center">
              <img 
                src={contractor.logoUrl} 
                alt={companyName} 
                className="max-h-20 max-w-48 object-contain"
              />
            </div>
          )}
          <div className="text-2xl font-bold text-green-600 mb-1 tracking-wide" style={{ fontFamily: "'Times New Roman', serif" }}>
            {companyName.toUpperCase()}
          </div>
          <div className="text-lg font-bold text-gray-800 mb-1 uppercase tracking-wide" style={{ fontFamily: "'Times New Roman', serif" }}>
            {documentTitle}
          </div>
          <div className="text-sm text-gray-600">
            Estimate #{documentNumber}
          </div>
        </div>

        {/* Info Grid - Two columns like PDF template */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Estimate Information */}
          <div>
            <h3 className="text-lg font-bold text-green-600 mb-4 pb-1 border-b-2 border-green-600" style={{ fontFamily: "'Times New Roman', serif" }}>
              Estimate Information
            </h3>
            <div className="space-y-2">
              {estimate.issueDate && (
                <div>
                  <span className="font-semibold text-gray-800">Issue Date:</span>{' '}
                  <span className="text-gray-700">{format(new Date(estimate.issueDate), 'MMM dd, yyyy')}</span>
                </div>
              )}
              {estimate.expiryDate && (
                <div>
                  <span className="font-semibold text-gray-800">Expiry Date:</span>{' '}
                  <span className="text-gray-700">{format(new Date(estimate.expiryDate), 'MMM dd, yyyy')}</span>
                </div>
              )}
              {estimate.clientSignature && (
                <div>
                  <span className="font-semibold text-gray-800">Status:</span>{' '}
                  <strong className="text-green-600">ACCEPTED & SIGNED</strong>
                </div>
              )}
            </div>
          </div>

          {/* Client Information */}
          <div>
            <h3 className="text-lg font-bold text-green-600 mb-4 pb-1 border-b-2 border-green-600" style={{ fontFamily: "'Times New Roman', serif" }}>
              Client Information
            </h3>
            <div className="space-y-2">
              {client && (
                <>
                  <div>
                    <span className="font-semibold text-gray-800">Name:</span>{' '}
                    <span className="text-gray-700">{client.firstName} {client.lastName}</span>
                  </div>
                  {client.email && (
                    <div>
                      <span className="font-semibold text-gray-800">Email:</span>{' '}
                      <span className="text-gray-700">{client.email}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div>
                      <span className="font-semibold text-gray-800">Phone:</span>{' '}
                      <span className="text-gray-700">{formatPhone(client.phone)}</span>
                    </div>
                  )}
                  {client.address && (
                    <div>
                      <span className="font-semibold text-gray-800">Address:</span>{' '}
                      <span className="text-gray-700">{client.address}{client.city ? `, ${client.city}` : ''}{client.state ? `, ${client.state}` : ''}{client.zip ? ` ${client.zip}` : ''}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Services & Pricing Section */}
        <div className="mb-8">
          <h3 className="text-lg font-bold text-green-600 mb-4 pb-1 border-b-2 border-green-600" style={{ fontFamily: "'Times New Roman', serif" }}>
            Services & Pricing
          </h3>
          <EstimateLineItems 
            items={estimate.items || []}
            estimateNumber={estimate.estimateNumber || estimate.estimate_number || 'N/A'}
            className="mb-6"
          />
          
          {/* Total Section - Right aligned like PDF */}
          <div className="text-right mt-6 mb-6 p-5 bg-gray-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700">
            <div className="mb-2">
              <span className="text-gray-800">Subtotal:</span>{' '}
              <strong className="text-gray-800">{formatCurrency(estimate.subtotal)}</strong>
            </div>
            {Number(estimate.tax) > 0 && (
              <div className="mb-2">
                <span className="text-gray-800">Tax ({estimate.tax}%):</span>{' '}
                <strong className="text-gray-800">{formatCurrency((Number(estimate.subtotal) * Number(estimate.tax) / 100))}</strong>
              </div>
            )}
            {Number(estimate.discount) > 0 && (
              <div className="mb-2">
                <span className="text-gray-800">Discount:</span>{' '}
                <strong className="text-gray-800">-{formatCurrency(Number(estimate.discount))}</strong>
              </div>
            )}
            <div className="mt-4 pt-4 border-t-2 border-green-600">
              <div className="text-lg mb-1">
                <span className="text-gray-800">Total Amount:</span>
              </div>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(estimate.total)}
              </div>
            </div>
          </div>
        </div>

        {/* Terms Section */}
        {estimate.terms && (
          <div className="mb-8">
            <h3 className="text-lg font-bold text-green-600 mb-4 pb-1 border-b-2 border-green-600" style={{ fontFamily: "'Times New Roman', serif" }}>
              Terms & Conditions
            </h3>
            <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
              {estimate.terms}
            </div>
          </div>
        )}

        {/* Notes Section */}
        {getDisplayNotes(estimate.notes) && (
          <div className="mb-8">
            <h3 className="text-lg font-bold text-green-600 mb-4 pb-1 border-b-2 border-green-600" style={{ fontFamily: "'Times New Roman', serif" }}>
              Additional Notes
            </h3>
            <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
              {getDisplayNotes(estimate.notes)}
            </div>
          </div>
        )}

        {/* Signature Section */}
        {estimate.clientSignature && (
          <div className="mb-8 mt-8 pt-8 border-t-2 border-green-600">
            <div className="text-center">
              <p className="text-gray-700 mb-4">
                Signed by: <span className="font-semibold text-green-600">{estimate.client?.firstName} {estimate.client?.lastName}</span>
              </p>
              <div className="inline-block p-6 bg-white border-2 border-gray-300 shadow-md">
                <img 
                  src={estimate.clientSignature} 
                  alt="Client signature" 
                  className="max-h-32 max-w-full object-contain" 
                  style={{ imageRendering: 'crisp-edges' as any }}
                />
              </div>
              <p className="text-gray-500 text-sm mt-4">
                Digitally signed on {estimate.updatedAt ? format(new Date(estimate.updatedAt), "MMMM d, yyyy 'at' h:mm a") : 'Unknown date'}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md mb-8">
            {error}
          </div>
        )}

        {/* Action Buttons Section */}
        <div className="mt-12 pt-8 border-t-2 border-gray-300">
            {/* Show different content based on estimate status */}
            {/* Hide action buttons if estimate is signed, accepted, converted, or rejected */}
            {estimate.status === 'accepted' || estimate.status === 'converted' || estimate.clientSignature ? (
              <div className="text-center">
                <div className="mb-4">
                  <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-green-800 mb-2">Estimate Accepted!</h3>
                  <p className="text-green-600 text-lg">This estimate has been successfully signed and accepted.</p>
                </div>
                
                {/* PDF Download Button for accepted estimates */}
                <div className="flex justify-center">
                  <Button
                    onClick={() => generateSignedPDF()}
                    className="bg-green-600 hover:bg-green-700 text-white py-4 px-8 text-lg rounded-lg shadow-lg transition-all hover:shadow-xl"
                  >
                    <FileText className="h-5 w-5 mr-2" />
                    Download Signed Estimate
                  </Button>
                </div>
              </div>
            ) : estimate.status === 'rejected' ? (
              <div className="text-center">
                <div className="mb-4">
                  <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-red-800 mb-2">Estimate Rejected</h3>
                  <p className="text-red-600 text-lg">This estimate has been rejected.</p>
                  {estimate.notes && (
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-left max-w-xl mx-auto">
                      <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">Reason for rejection:</p>
                      <p className="text-red-700 dark:text-red-300 whitespace-pre-wrap">{estimate.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : estimate.status === 'converted' ? (
              <div className="text-center">
                <div className="mb-4">
                  <CheckCircle className="h-16 w-16 text-purple-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-purple-800 mb-2">Estimate Converted to Invoice</h3>
                  <p className="text-purple-600 text-lg">This estimate has been signed and converted to an invoice.</p>
                </div>
                
                {/* PDF Download Button for converted estimates */}
                <div className="flex justify-center">
                  <Button
                    onClick={() => generateSignedPDF()}
                    className="bg-purple-600 hover:bg-purple-700 text-white py-4 px-8 text-lg rounded-lg shadow-lg transition-all hover:shadow-xl"
                  >
                    <FileText className="h-5 w-5 mr-2" />
                    Download Signed Estimate
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-2">
                  <h3 className="text-xl font-bold text-green-800">Ready to Move Forward?</h3>
                  <p className="text-green-700 mt-2">
                    Review the estimate above and let us know your decision. 
                    Accepting will allow us to schedule your project and begin work.
                  </p>
                  <p className="text-green-600 text-sm mt-2">Select an option below:</p>
                </div>
                
                <div className="flex flex-col gap-4 w-full max-w-2xl mx-auto">
                  {/* PDF Download Button */}
                  <div className="flex justify-center mb-4">
                    <Button
                      onClick={() => {
                        try {
                          if (!estimate) {
                            toast({
                              title: "Error",
                              description: "Estimate data is not available.",
                              variant: "destructive"
                            });
                            return;
                          }

                          const config = pdfConfig ? { ...DEFAULT_PDF_TEMPLATE_CONFIG, ...pdfConfig } : DEFAULT_PDF_TEMPLATE_CONFIG;
                          downloadEstimatePdf(estimate, config);
                          
                          toast({
                            title: "PDF Download",
                            description: "Print dialog opened. Select 'Save as PDF' to download.",
                          });
                        } catch (error) {
                          console.error('Error downloading PDF:', error);
                          toast({
                            title: "Error",
                            description: error instanceof Error ? error.message : "Failed to download PDF. Please try again.",
                            variant: "destructive"
                          });
                        }
                      }}
                      variant="outline"
                      className="bg-white text-green-600 border-green-300 hover:bg-green-50 py-3 px-6 rounded-lg shadow-sm"
                    >
                      <FileText className="h-5 w-5 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <Button
                      onClick={() => setRejectDialogOpen(true)}
                      className="w-full sm:w-1/3 bg-red-600 hover:bg-red-700 text-white py-8 text-xl h-auto rounded-lg shadow-lg transition-all hover:shadow-xl relative overflow-hidden border-2 border-red-500"
                    >
                      <div className="absolute inset-0 bg-white/10 flex items-center justify-center">
                        <div className="animate-pulse bg-white/5 w-full h-full"></div>
                      </div>
                      <div className="relative z-10 flex items-center justify-center gap-2">
                        <XCircle className="h-6 w-6" />
                        <span>REJECT ESTIMATE</span>
                      </div>
                    </Button>
                    
                    <Button
                      onClick={() => setAcceptDialogOpen(true)}
                      className="w-full sm:w-2/3 bg-green-600 hover:bg-green-700 text-white py-8 text-xl h-auto rounded-lg shadow-lg transition-all hover:shadow-xl relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-white/10 flex items-center justify-center">
                        <div className="animate-pulse bg-white/5 w-full h-full"></div>
                      </div>
                      <div className="relative z-10 flex items-center justify-center gap-2">
                        <CheckCircle className="h-6 w-6" />
                        <span>APPROVE ESTIMATE</span>
                      </div>
                    </Button>
                  </div>
                </div>
              </>
            )}
        </div>
      </div>

        {/* Diálogo de aceptación */}
        <Dialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl text-green-700 flex items-center gap-2">
                <CheckCircle className="h-6 w-6" />
                Confirm Approval
              </DialogTitle>
              <DialogDescription className="text-base">
                By approving this estimate, you are authorizing the contractor to proceed with the work.
              </DialogDescription>
            </DialogHeader>
            <div className="py-6 space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-green-800 font-medium">Total to pay:</span>
                  <span className="text-2xl font-bold text-green-800">{formatCurrency(estimate.total)}</span>
                </div>
                <p className="text-sm text-green-700">
                  This is the total amount agreed for the work described in this estimate.
                </p>
              </div>
              
            </div>
            <DialogFooter className="gap-3 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setAcceptDialogOpen(false)}
                disabled={actionInProgress}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => setSignatureDialogOpen(true)}
                disabled={actionInProgress}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-lg"
              >
                {actionInProgress ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Sign & Approve Estimate
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diálogo de rechazo */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl text-red-700 flex items-center gap-2">
                <XCircle className="h-6 w-6" />
                Confirm Rejection
              </DialogTitle>
              <DialogDescription className="text-base">
                Please help us improve by indicating the reason for your rejection.
              </DialogDescription>
            </DialogHeader>
            <div className="py-6 space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="font-medium text-red-800 mb-2">Reason for rejection:</p>
                <Textarea
                  placeholder="Please briefly explain why you are not satisfied with this estimate..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[100px] border-red-200 focus:border-red-400 focus:ring-red-400"
                />
                {!notes && (
                  <p className="text-sm text-red-600 mt-2">
                    * This field is required to reject the estimate
                  </p>
                )}
              </div>
              
              <div className="border-t border-gray-200 py-4">
                <p className="text-gray-500 text-sm italic">
                  Your comments will help the contractor improve their services. Thank you for your feedback.
                </p>
              </div>
            </div>
            <DialogFooter className="gap-3 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setRejectDialogOpen(false)}
                disabled={actionInProgress}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => handleAction('reject')}
                disabled={actionInProgress || !notes}
                className="w-full sm:w-auto text-lg"
              >
                {actionInProgress ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <XCircle className="mr-2 h-5 w-5" />
                    Reject Estimate
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Signature Dialog - Centered & Mobile Optimized */}
        <Dialog open={signatureDialogOpen} onOpenChange={(open) => !actionInProgress && setSignatureDialogOpen(open)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="text-center">
              <DialogTitle className="text-xl sm:text-2xl text-green-500 flex items-center justify-center gap-2">
                <CheckCircle className="h-6 w-6" />
                Digital Signature Required
              </DialogTitle>
              <DialogDescription className="text-sm sm:text-base">
                Please provide your digital signature to approve this estimate.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 sm:py-6 relative">
              {/* Loading overlay when submitting */}
              {actionInProgress && (
                <div className="absolute inset-0 bg-white/90 dark:bg-slate-900/90 z-10 flex flex-col items-center justify-center rounded-lg">
                  <Loader2 className="h-12 w-12 animate-spin text-green-600 mb-4" />
                  <p className="text-lg font-medium text-green-800 dark:text-green-200">Submitting your signature...</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Please wait.</p>
                </div>
              )}
              <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200 mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mb-2">
                  <span className="text-green-800 font-semibold text-base sm:text-lg">Total to pay:</span>
                  <span className="text-2xl sm:text-3xl font-bold text-green-600">{formatCurrency(estimate.total)}</span>
                </div>
                <p className="text-sm sm:text-base text-green-700 text-center">
                  By signing, you authorize the contractor to proceed with the work described in this estimate.
                </p>
              </div>
              
              {/* Centered Wider Signature Area */}
              <div className="flex justify-center w-full">
                <div className="w-full max-w-2xl px-2">
                  <SignaturePad
                    onSave={(signature) => {
                      setSignatureData(signature);
                      // Do not close dialogs here; handleActionWithSignature will close them on success
                      handleActionWithSignature('accept', signature);
                    }}
                    onCancel={() => setSignatureDialogOpen(false)}
                    width={Math.min(600, window.innerWidth - 60)}
                    height={280}
                  />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Success Dialog */}
        <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-3xl text-green-700 flex items-center gap-3">
                <CheckCircle className="h-8 w-8" />
                Estimate Successfully Accepted!
              </DialogTitle>
              <DialogDescription className="text-lg text-gray-700">
                Your digital signature has been recorded and the estimate is now legally binding.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-6 space-y-6">
              {/* Contract Details */}
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-4">Contract Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700 font-medium">Estimate Number:</span>
                    <span className="text-lg font-bold text-green-800">{estimate.estimateNumber}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-700 font-medium">Total Amount:</span>
                    <span className="text-xl font-bold text-green-800">{formatCurrency(estimate.total)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-700 font-medium">Date Signed:</span>
                    <span className="text-green-800 font-medium">{new Date().toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-green-700 font-medium">Signed By:</span>
                    <span className="text-green-800 font-medium">{estimate.client?.firstName} {estimate.client?.lastName}</span>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-3">What Happens Next?</h3>
                <ul className="space-y-2 text-green-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Contractor will be notified of your acceptance</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Work will begin according to the agreed timeline</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Invoice will be generated upon project completion</span>
                  </li>
                </ul>
              </div>

              {/* Legal Notice */}
              <div className="bg-gray-50 dark:bg-slate-800/50 p-4 rounded-lg border border-gray-200 dark:border-slate-700">
                <p className="text-sm text-gray-600 text-center">
                  <strong>Legal Notice:</strong> This signed document serves as a legally binding contract. 
                  Please keep a copy for your records.
                </p>
              </div>
            </div>
            
            <DialogFooter className="gap-3 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setSuccessDialogOpen(false)}
                className="w-full sm:w-auto"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  generateSignedPDF();
                  setSuccessDialogOpen(false);
                }}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-lg py-3"
              >
                <FileText className="mr-2 h-5 w-5" />
                Download Signed Estimate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
}